/**
 * <s-review-scroller> — arrow buttons for a horizontal scroll row
 *
 * Markup contract:
 *   [data-scroller]  the scrolling row; its children are the cards
 *   [data-prev]      button that scrolls back by one card
 *   [data-next]      button that scrolls forward by one card
 *
 * Buttons are disabled at the start and end of the row. Native scrolling,
 * swiping and keyboard scrolling keep working alongside the buttons.
 */
class SReviewScroller extends HTMLElement {
  #ac;
  #row;
  #prev;
  #next;

  connectedCallback() {
    this.#row = this.querySelector('[data-scroller]');
    this.#prev = this.querySelector('[data-prev]');
    this.#next = this.querySelector('[data-next]');
    if (!this.#row || !this.#prev || !this.#next) return;

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#prev.addEventListener('click', () => this.#step(-1), { signal });
    this.#next.addEventListener('click', () => this.#step(1), { signal });
    this.#row.addEventListener('scroll', () => this.#update(), { signal, passive: true });
    window.addEventListener('resize', () => this.#update(), { signal, passive: true });

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #step(direction) {
    const card = this.#row.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.#row).columnGap) || 0;
    const distance = card ? card.getBoundingClientRect().width + gap : this.#row.clientWidth;
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#row.scrollBy({ left: distance * direction * rtl, behavior: reduce ? 'auto' : 'smooth' });
  }

  #update() {
    const max = this.#row.scrollWidth - this.#row.clientWidth;
    const position = Math.abs(this.#row.scrollLeft);
    this.#prev.disabled = position <= 1;
    this.#next.disabled = position >= max - 1;
  }
}

if (!customElements.get('s-review-scroller')) {
  customElements.define('s-review-scroller', SReviewScroller);
}
